import { useTranslation } from "react-i18next";
import { TAG_TYPES } from "@src/constants";
import {
  ResponseSessionTypeResources,
  ResponseTagsExpertTag,
} from "@src/api/v1/data-contracts";
import { FC, useMemo } from "react";
import { ReactSVG } from "react-svg";
import Spinner from "@openup/shared/components/Spinner/Spinner";
import { ChevronRight } from "@mui/icons-material";
import BookingContainer from "./BookingContainer";

type SessionTypeCardProps = {
  cardTitle?: string;
  cardSubtitle?: string;
  className?: string;
  imageUrl: string;
  onClick: () => void;
};

const SessionTypeCard: FC<SessionTypeCardProps> = ({
  cardTitle = "",
  cardSubtitle = "",
  className = "",
  imageUrl,
  onClick = () => {},
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`
      flex w-full items-start gap-4 border-solid border-slate-200
            bg-white items-center
            shadow-[0px_0px_20px_0px_rgba(51,51,51,0.1)]
            p-6 rounded-2xl cursor-pointer
      ${className}
    `}
  >
    <ReactSVG
      src={imageUrl}
      title={cardTitle}
      className={`w-16 h-16 ${className}`}
    />
    <div className="flex flex-col items-start gap-1 flex-1">
      <p className="text-indigo-800 sub1 font-semibold">
        {cardTitle}
        <ChevronRight className="ml-1.5 w-6 h-6 align-middle" />
      </p>
      <p className="text-slate-700 body2 text-sm font-medium text-left">
        {cardSubtitle}
      </p>
    </div>
  </button>
);

interface SessionTypeProps {
  moveToNextStep: () => void;
  setSessionType: (sessionType: string) => void;
  psychologistTags: ResponseTagsExpertTag[] | undefined;
  staticResourcesUrls: ResponseSessionTypeResources | undefined;
}

const SessionType: React.FC<SessionTypeProps> = ({
  moveToNextStep,
  setSessionType,
  psychologistTags,
  staticResourcesUrls,
}) => {
  const { t } = useTranslation();

  const handlePressCard = (sessionType: string) => {
    setSessionType(sessionType);
    moveToNextStep();
  };

  const filteredSessionTypes = useMemo(() => {
    return psychologistTags?.filter(
      (tag) =>
        tag?.tagType?.toLowerCase() === TAG_TYPES.SESSION_TYPE.toLowerCase(),
    );
  }, [psychologistTags]);

  return (
    <BookingContainer title={t("Booking.ForClient.SessionType.Title")}>
      {!filteredSessionTypes && (
        <div className="absolute inset-0 m-auto flex items-center justify-center">
          <Spinner />
        </div>
      )}
      {filteredSessionTypes && (
        <div className="flex flex-col w-xl justify-center items-center gap-6 self-stretch">
          {filteredSessionTypes.map((tag) => (
            <SessionTypeCard
              onClick={() => handlePressCard(tag.tagName!)}
              cardTitle={t(`Booking.SessionType.${tag.tagName}.Description`)}
              cardSubtitle={t(`Booking.SessionType.${tag.tagName}.Details`)}
              imageUrl={`${staticResourcesUrls?.bookingImagesUrl}/${tag.tagName}.svg`}
              key={tag.tagName}
            />
          ))}
        </div>
      )}
    </BookingContainer>
  );
};

export default SessionType;
